import { Box, Button, Collapse, Group, Tooltip, Text } from "@mantine/core"
import { FlowReview } from "./approval-flow.tsx"
import ReactFlow, {
  Handle,
  NodeProps,
  Position,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "reactflow"
import Dagre from "@dagrejs/dagre"
import { toFlowEdge } from "../../routes/app/admin-settings/absences/-components/shared.ts"
import {
  AbsentApprovalGraph,
  AbsentApprovalNode,
} from "../../@client/Absents.ts"
import { FSharpOption } from "../../@client/Microsoft_FSharp_Core.ts"
import { Graph, Node } from "../../@client/Theasoft_Absences_Workflow.ts"
import { ApprovalAndWorkflowNode } from "../../@client/Theasoft_Absences.ts"

function toFlowNode(activeNode: FSharpOption<AbsentApprovalNode>) {
  return (node: AbsentApprovalNode, graphNode: Node) => {
    return {
      id: node.nodeId,
      data: {
        approvalNode: node,
        graphNode,
        isActive: activeNode?.nodeId === node.nodeId,
      },
      position: { x: 0, y: 0 },
      type: "approval" as const,
    }
  }
}

const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({ label: "" }))

const nodeWidth = 230

const nodeHeight = 100
const getLayoutedElements = (nodes: any, edges: any, options: any) => {
  g.setGraph({ rankdir: options.direction })

  edges.forEach((edge: any) => g.setEdge(edge.source, edge.target))
  nodes.forEach((node: any) =>
    g.setNode(node.id, { width: nodeWidth, height: nodeHeight })
  )

  Dagre.layout(g)

  return {
    nodes: nodes.map((node: any) => {
      const { x, y } = g.node(node.id)

      return { ...node, position: { x, y } }
    }),
    edges,
  }
}
type CustomNodeProps = NodeProps<{
  approvalNode: AbsentApprovalNode
  graphNode: FSharpOption<Node>
}>
function RenderNode(props: CustomNodeProps) {
  return (
    <div>
      <Handle type="target" position={Position.Left} />
      <Handle type="source" position={Position.Right} />
      <Tooltip label={JSON.stringify(props, null, 2)}>
        <div>
          <Text>
            {props.data.approvalNode.nodeId}{" "}
            {props.data.approvalNode.details.Case}
          </Text>
          <Text>
            {props.data.graphNode?.nodeId} {props.data.graphNode?.status.Case}{" "}
            {props.data.graphNode?.name}
          </Text>
        </div>
      </Tooltip>
    </div>
  )
}
export function AbsenceLeaveRequestDebugGraph({
  graph,
  nodes: nodesWithStatus,
}: {
  graph: Graph
  nodes: ApprovalAndWorkflowNode[]
}) {
  const initialNodes = React.useMemo(
    () =>
      (nodesWithStatus || []).map((x) =>
        toFlowNode(null)(x.approvalNode, x.workflowNode)
      ),
    []
  )

  const initialEdges = React.useMemo(
    () => graph.edges.map((v) => toFlowEdge(v)),
    []
  )
  const [nodes, setNodes] = useNodesState(initialNodes)
  const [edges, setEdges] = useEdgesState(initialEdges)
  const { fitView } = useReactFlow()

  React.useEffect(() => {
    const layouted = getLayoutedElements(nodes, edges, { direction: "LR" })
    setNodes([...layouted.nodes])
    setEdges([...layouted.edges])
    window.requestAnimationFrame(() => {
      fitView()
    })
  }, [])

  const nodeTypes = React.useMemo(
    () => ({
      //trigger: TriggerNode,
      approval: RenderNode,
    }),
    []
  )

  return (
    <Box style={{ width: "100%", height: 400 }}>
      <ReactFlow
        proOptions={{ hideAttribution: true }}
        nodeTypes={nodeTypes}
        nodes={nodes}
        edges={edges}
        snapToGrid
      />
    </Box>
  )
}

export function NodesEdgesSimpleFlow({
  graph,
}: {
  graph: AbsentApprovalGraph
}) {
  const [opened, setOpened] = React.useState(false)
  return (
    <Box w="100%">
      <Group justify="center" mb={5}>
        <Button size="xs" variant="subtle" onClick={() => setOpened(!opened)}>
          Details
        </Button>
      </Group>

      <Collapse in={opened}>
        <ReactFlowProvider>
          <FlowReview data={graph} activeNode={null} />
        </ReactFlowProvider>
      </Collapse>
    </Box>
  )
}
